import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useState, useEffect } from 'preact/hooks';
import { useForm } from 'react-hook-form';
import { Edit, Trash2, Mail } from 'lucide-preact';
import { StatusBadge } from '../../common/StatusBadge';
import { FormActions } from '@/components';
import { WorkspaceFormFields } from './WorkspaceFormFields';
export function WorkspaceCard({ workspace, onDelete, onUpdate, loading = false }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editingWorkspace, setEditingWorkspace] = useState(null);
    const { register, control, handleSubmit, reset, watch, setValue, formState: { errors, isValid, isDirty } } = useForm({
        mode: 'onChange',
        defaultValues: { Name: '', Slug: '', Description: '', Status: 'Active', TimeZone: '', DailyLeadsSummaryEnabled: false, DailyLeadsSummaryHour: 8 },
    });
    const handleEdit = () => {
        const initial = {
            Id: workspace.Id,
            ETag: workspace.ETag,
            ClientCompanyId: workspace.ClientCompanyId,
            Name: workspace.Name,
            Slug: workspace.Slug,
            Description: workspace.Description || '',
            Status: workspace.Status || 'Active',
            TimeZone: workspace.TimeZone,
            DailyLeadsSummaryEnabled: workspace.DailyLeadsSummaryEnabled,
            DailyLeadsSummaryHour: workspace.DailyLeadsSummaryHour,
        };
        setEditingWorkspace(initial);
        reset({
            Name: initial.Name,
            Slug: initial.Slug,
            Description: initial.Description || '',
            Status: initial.Status || 'Active',
            TimeZone: initial.TimeZone,
            DailyLeadsSummaryEnabled: initial.DailyLeadsSummaryEnabled,
            DailyLeadsSummaryHour: initial.DailyLeadsSummaryHour,
        });
        setIsEditing(true);
    };
    const onSubmit = async (values) => {
        if (!editingWorkspace)
            return;
        const payload = {
            ...editingWorkspace,
            Name: values.Name,
            Description: values.Description,
            Status: values.Status,
            TimeZone: values.TimeZone,
            DailyLeadsSummaryEnabled: values.DailyLeadsSummaryEnabled,
            DailyLeadsSummaryHour: Number(values.DailyLeadsSummaryHour),
        };
        const success = await onUpdate(payload);
        if (success) {
            setIsEditing(false);
            setEditingWorkspace(null);
        }
    };
    const handleCancel = () => {
        setIsEditing(false);
        setEditingWorkspace(null);
    };
    // Keep local editingWorkspace in sync with form watch values
    const wName = watch('Name');
    const wDesc = watch('Description');
    const wStatus = watch('Status');
    const wTimeZone = watch('TimeZone');
    const wDailyLeads = watch('DailyLeadsSummaryEnabled');
    const wDailyHour = watch('DailyLeadsSummaryHour');
    useEffect(() => {
        if (isEditing && editingWorkspace) {
            setEditingWorkspace({
                ...editingWorkspace,
                Name: wName || '',
                Description: wDesc || '',
                Status: wStatus || 'Active',
                TimeZone: wTimeZone || '',
                DailyLeadsSummaryEnabled: wDailyLeads,
                DailyLeadsSummaryHour: Number(wDailyHour),
            });
        }
    }, [wName, wDesc, wStatus, wTimeZone, wDailyLeads, wDailyHour]);
    return (_jsx("div", { className: "bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors duration-200", children: _jsx("div", { className: "p-4", children: _jsxs("div", { className: "flex items-start justify-between", children: [_jsx("div", { className: "flex items-start gap-3 flex-1 min-w-0", children: _jsx("div", { className: "flex-1 min-w-0", children: isEditing ? (_jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "space-y-3", children: [_jsx(WorkspaceFormFields, { control: control, register: register, errors: errors, watch: watch, setValue: setValue, slugLocked: true }), _jsx(FormActions, { isSubmitting: loading, isSaveDisabled: !isValid || !isDirty, onCancel: handleCancel, saveText: "Save Changes", className: "px-0 py-1 border-0 bg-transparent dark:bg-transparent" })] })) : (_jsxs(_Fragment, { children: [_jsx("h4", { className: "font-semibold text-slate-900 dark:text-white text-lg", children: workspace.Name }), _jsxs("div", { className: "flex items-center gap-4 mt-1", children: [_jsx("span", { className: "text-sm text-slate-600 dark:text-slate-300 line-clamp-1", children: workspace.Description || 'No description' }), _jsx(StatusBadge, { status: workspace.Status || 'Unknown', variant: "light" }), workspace.TimeZone && (_jsx("span", { className: "text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap", children: workspace.TimeZone }))] }), workspace.DailyLeadsSummaryEnabled && (_jsxs("div", { className: "flex items-center gap-1.5 mt-1.5", children: [_jsx(Mail, { className: "w-3.5 h-3.5 text-primary-600 dark:text-primary-400" }), _jsx("span", { className: "text-xs text-slate-600 dark:text-slate-400", children: "Daily leads summary enabled" })] }))] })) }) }), _jsx("div", { className: "flex items-center gap-1 ml-3", children: !isEditing && (_jsxs(_Fragment, { children: [_jsx("button", { onClick: handleEdit, className: "p-2 text-slate-600 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors", title: "Edit Workspace", children: _jsx(Edit, { className: "w-4 h-4" }) }), _jsx("button", { onClick: () => onDelete(workspace.Id), className: "p-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors", title: "Delete Workspace", children: _jsx(Trash2, { className: "w-4 h-4" }) })] })) })] }) }) }));
}
